import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Landmark, FileSearch, Scale } from 'lucide-react';
import { URL_SOLICITAR_ACCESO } from '../../lib/urbanismoBase';
import { IlusZonas, IlusCapas } from './Ilustraciones';

/**
 * Secciones de la home de plataforma. Urbanismo va primero y Territorio tiene su
 * sección propia con el mismo peso. No se publican cifras de cobertura de Urbanismo
 * (número de municipios): crecen cada semana y envejecerían el copy.
 * Las ilustraciones se dibujan al entrar en pantalla (data-dibuja).
 *
 * Cada idea se dice una vez (2026-10-13): la definición de GeoVis vive en el héroe y en
 * la primera pregunta frecuente, el detalle de Territorio en /territorio y el de la
 * ficha en /urbanismo. Aquí, dibujo + una frase + el botón.
 */

const Eyebrow = ({ children }) => (
  <p className="text-xs font-medium uppercase tracking-wider text-acento">{children}</p>
);

/* ── Las dos puertas, con el mismo peso ── */
const Puerta = ({ tinte, Ilus, eyebrow, titulo, texto, cta, secundario }) => (
  <article className={`${tinte} puerta group flex flex-col overflow-hidden`}>
    <div data-dibuja className="flex h-64 items-center justify-center border-b border-border bg-[oklch(var(--nivel0))] px-8 py-6">
      <div className="w-full max-w-[300px] [&>svg]:max-h-52">
        <Ilus />
      </div>
    </div>
    <div className="flex flex-1 flex-col p-7">
      <span className="block h-0.5 w-[22px] bg-acento" aria-hidden="true" />
      <p className="mt-3 text-xs font-medium uppercase tracking-wider text-acento">{eyebrow}</p>
      <h3 className="mt-1 font-display text-3xl text-foreground">{titulo}</h3>
      <p className="mt-3 leading-relaxed text-muted-foreground">{texto}</p>
      <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 pt-7">
        {cta}
        {secundario}
      </div>
    </div>
  </article>
);

export const DosPuertas = () => (
  <section aria-labelledby="dos-puertas" className="mx-auto max-w-7xl px-6 pt-20 sm:pt-24">
    <div className="max-w-2xl">
      <h2 id="dos-puertas" className="text-3xl leading-tight text-foreground sm:text-4xl">
        Dos preguntas sobre cada parcela. Una plataforma.
      </h2>
    </div>
    <div className="mt-10 grid gap-6 lg:grid-cols-2">
      <Puerta
        tinte="tinte-urbanismo"
        Ilus={IlusZonas}
        eyebrow="Qué se puede construir"
        titulo="GeoVis Urbanismo"
        texto="La ficha urbanística de cada parcela, leída del plan vigente y con la cita de cada cifra."
        cta={
          <Link to={URL_SOLICITAR_ACCESO} className="btn-vivo inline-flex h-11 items-center rounded-lg bg-primary px-5 font-medium text-primary-foreground hover:bg-primary/90">
            Solicitar acceso <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        }
        secundario={<Link to="/urbanismo" className="font-medium text-acento hover:underline">Conocer Urbanismo</Link>}
      />
      <Puerta
        tinte="tinte-territorio"
        Ilus={IlusCapas}
        eyebrow="Qué afecta al terreno"
        titulo="GeoVis Territorio"
        texto="El visor GIS que cruza las capas oficiales sobre un mismo mapa para decidir la viabilidad de un emplazamiento."
        cta={
          <Link to="/territorio" className="btn-vivo inline-flex h-11 items-center rounded-lg bg-primary px-5 font-medium text-primary-foreground hover:bg-primary/90">
            Ver GeoVis Territorio <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        }
        secundario={<Link to="/territorio#precios" className="font-medium text-acento hover:underline">Planes y precios</Link>}
      />
    </div>
    <Link
      to="/informe"
      className="tinte-informe mt-6 flex flex-col gap-2 rounded-lg border border-border bg-card px-7 py-5 transition-colors duration-200 hover:border-acento/40 sm:flex-row sm:items-center sm:justify-between"
    >
      <span>
        <span className="font-display text-2xl text-foreground">GeoVis Informe</span>
        <span className="ml-3 text-muted-foreground">el dossier de viabilidad en PDF, en menos de 24 horas</span>
      </span>
      <span className="inline-flex items-center gap-1.5 font-medium text-acento">
        Ver el informe <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </span>
    </Link>
  </section>
);

/* ── Cómo funciona Urbanismo: tres pasos y la ficha con sus citas, en una banda ── */
const PASOS = ['Leemos el plan entero', 'Lo atamos a cada parcela', 'Cada cifra, con su cita'];

export const ComoFunciona = () => (
  <section aria-labelledby="como-funciona" className="banda-tinta mt-24 sm:mt-28">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'oklch(var(--banda-acento))' }}>
          GeoVis Urbanismo · cómo funciona
        </p>
        <h2 id="como-funciona" className="mt-3 max-w-2xl text-3xl leading-tight sm:text-[2.75rem]" style={{ color: 'oklch(var(--banda-texto))' }}>
          Del plan escaneado a una ficha que aguanta un comité.
        </h2>
        <ol className="mt-10 space-y-5">
          {PASOS.map((titulo, i) => (
            <li key={titulo} className="flex items-baseline gap-5 border-t pt-5" style={{ borderColor: 'oklch(var(--banda-linea))' }}>
              <span className="cifra font-display text-3xl" style={{ color: 'oklch(var(--banda-acento))' }}>{i + 1}</span>
              <h3 className="font-display text-2xl" style={{ color: 'oklch(var(--banda-texto))' }}>{titulo}</h3>
            </li>
          ))}
        </ol>
      </div>
      <div className="lg:col-span-5" data-dibuja>
        <div className="rounded-lg p-6" style={{ background: 'oklch(var(--banda-celda))', border: '1px solid oklch(var(--banda-linea))' }}>
          <IlusZonas />
          <div className="mt-5 space-y-2.5">
            {['PGOU · art. 42 · pág. 118', 'Normas · art. 156.2 · pág. 301', 'Ordenanza 13b · pág. 304'].map((c, i) => (
              <div key={c} className="relleno flex items-center justify-between gap-3 text-xs" style={{ '--d': `${1300 + i * 200}ms`, color: 'oklch(var(--banda-suave))' }}>
                <span className="h-1.5 flex-1 rounded-sm" style={{ background: 'oklch(var(--banda-linea))' }} />
                <span className="rounded-sm px-1.5 py-0.5" style={{ background: 'oklch(var(--banda-acento) / 0.15)', color: 'oklch(var(--banda-acento))' }}>{c}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs" style={{ color: 'oklch(var(--banda-suave))' }}>Ejemplo ilustrativo</p>
        </div>
        <Link to="/urbanismo" className="mt-5 inline-flex items-center gap-1.5 font-medium hover:underline" style={{ color: 'oklch(var(--banda-acento))' }}>
          Qué incluye la ficha <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);

/* ── GeoVis Territorio en la home. Conserva las anclas antiguas de la home
   (/#producto, /#features, /#precios) para que los enlaces externos no se rompan. ── */
export const SeccionTerritorio = () => (
  <section id="producto" aria-labelledby="territorio-home" className="tinte-territorio mx-auto max-w-7xl px-6 py-24">
    <span id="features" className="block" aria-hidden="true" />
    <span id="precios" className="block" aria-hidden="true" />
    <div className="grid items-center gap-12 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <Eyebrow>GeoVis Territorio</Eyebrow>
        <h2 id="territorio-home" className="mt-3 text-3xl leading-tight text-foreground sm:text-[2.75rem]">
          Todo lo que afecta al terreno, en un mismo mapa.
        </h2>
        <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
          Más de 80 capas oficiales en un mapa, en 15 países. 7 días de prueba gratis.
        </p>
        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
          <Link to="/territorio" className="btn-vivo inline-flex h-11 items-center rounded-lg bg-primary px-5 font-medium text-primary-foreground hover:bg-primary/90">
            Ver GeoVis Territorio <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
          <Link to="/territorio#precios" className="inline-flex h-11 items-center font-medium text-acento hover:underline">Planes y precios</Link>
          <Link to="/renovables" className="inline-flex h-11 items-center font-medium text-muted-foreground hover:text-foreground">Renovables</Link>
        </div>
      </div>
      {/* En móvil ya se vio en la puerta de Territorio: no se repite. */}
      <div className="hidden lg:col-span-6 lg:block" data-dibuja>
        <div className="mx-auto max-w-[480px]">
          <IlusCapas grande />
        </div>
      </div>
    </div>
  </section>
);

/* ── Para quién ── */
const AUDIENCIAS = [
  { icon: Building2, titulo: 'Promotoras e inversores' },
  { icon: Landmark, titulo: 'Estudios de arquitectura' },
  { icon: FileSearch, titulo: 'Due diligence y tasación' },
  { icon: Scale, titulo: 'Ingenierías y energéticas' },
];

export const Audiencias = () => (
  <section aria-labelledby="audiencias" className="border-t border-border bg-card/50">
    <div className="mx-auto max-w-7xl px-6 py-16">
      <h2 id="audiencias" className="max-w-2xl text-3xl leading-tight text-foreground sm:text-4xl">
        Para quien firma con el número delante.
      </h2>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {AUDIENCIAS.map(({ icon: Icon, titulo }) => (
          <li key={titulo} className="puerta flex items-center gap-3 rounded-lg px-5 py-4">
            <Icon className="h-5 w-5 shrink-0 text-[oklch(var(--primary-text))]" aria-hidden="true" />
            <span className="font-medium text-foreground">{titulo}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ── Cierre con las dos puertas ── */
export const Cierre = () => (
  <section className="mx-auto max-w-7xl px-6 pb-8">
    <div className="flex flex-col gap-8 rounded-lg border border-border bg-card p-8 sm:p-12 md:flex-row md:items-center md:justify-between">
      <div className="max-w-2xl">
        <h2 className="text-3xl text-foreground">¿Por dónde empiezas?</h2>
        <p className="mt-3 text-lg leading-relaxed text-muted-foreground">
          Dinos qué municipio te interesa y te enseñamos una parcela real.
        </p>
      </div>
      <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
        <Link to={URL_SOLICITAR_ACCESO} className="btn-vivo inline-flex h-12 items-center justify-center rounded-lg bg-primary px-6 font-medium text-primary-foreground hover:bg-primary/90">
          Solicitar acceso
        </Link>
        <Link to="/territorio" className="inline-flex h-12 items-center justify-center rounded-lg border border-border px-6 font-medium text-foreground hover:bg-accent">
          Probar Territorio gratis
        </Link>
      </div>
    </div>
  </section>
);

/* ── Preguntas de la home: las dos puertas (las del visor, completas, en /territorio) ── */
export const FAQ_HOME = [
  {
    q: '¿Qué es GeoVis?',
    a: 'Una plataforma para decidir sobre el suelo con datos oficiales. Tiene tres productos: GeoVis Urbanismo, que responde qué se puede construir en cada parcela citando artículo y página del plan; GeoVis Territorio, el visor GIS con más de 80 capas oficiales en 15 países; y GeoVis Informe, el dossier de viabilidad en PDF en menos de 24 horas.',
  },
  {
    q: '¿Está mi municipio en GeoVis Urbanismo?',
    a: 'La cobertura crece municipio a municipio, en España e Italia. Solicita acceso indicando tus municipios de interés y te decimos si ya están o cuándo entran: priorizamos por demanda.',
  },
  {
    q: '¿De dónde salen los datos?',
    a: 'Solo de documentos y servicios oficiales: el plan vigente y sus modificaciones, los servicios de planeamiento de cada comunidad, el Catastro, el IGN, los organismos ambientales y los gestores de red. En Urbanismo, cuando dos fuentes chocan manda la de más rango: una sentencia sobre el plan, el plan sobre sus fichas y estas sobre el visor.',
  },
  {
    q: '¿En qué países está GeoVis Territorio?',
    a: 'En 15: España, Portugal, Italia, Francia, Rumanía, Reino Unido, Noruega, Polonia, República Checa, Chile, Perú, México, Colombia, Ecuador y Sudáfrica, cada uno con sus fuentes oficiales de catastro, medio ambiente e infraestructuras.',
  },
  {
    q: '¿Cuánto cuesta?',
    a: 'GeoVis Territorio tiene un plan Free, Pro por 49 €/mes y Premium por 99 €/mes, con 7 días de prueba; el informe PDF se paga por proyecto desde 99 €. GeoVis Urbanismo se contrata solicitando acceso con tus municipios de interés.',
  },
];
